import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  calibrationService,
  CalibrationSchedule,
  type CalibrationRecordInput,
  type ExternalVerificationInput,
  type QualityReviewStatus,
  type TmmdeEquipmentFilters,
  type TmmdeHoldCause,
  type TmmdeStatusAction,
} from '../services/calibration.service';
import { message } from 'antd';

// Query keys
export const calibrationKeys = {
  all: ['calibration'] as const,
  schedules: (filters?: any) => [...calibrationKeys.all, 'schedules', filters] as const,
  schedule: (id: string) => [...calibrationKeys.all, 'schedule', id] as const,
  records: (filters?: any) => [...calibrationKeys.all, 'records', filters] as const,
  record: (id: string) => [...calibrationKeys.all, 'record', id] as const,
  equipmentList: (filters?: any) => [...calibrationKeys.all, 'equipment', 'list', filters] as const,
  equipment: (assetId: string) => [...calibrationKeys.all, 'equipment', 'detail', assetId] as const,
  impactList: (filters?: any) => [...calibrationKeys.all, 'impact', 'list', filters] as const,
  impact: (id: string) => [...calibrationKeys.all, 'impact', 'detail', id] as const,
};

const errorText = (e: any, fallback: string) => e?.response?.data?.message || e?.message || fallback;

export function useCalibrationSchedules(filters?: { status?: string; equipment_id?: string }) {
  return useQuery({
    queryKey: calibrationKeys.schedules(filters),
    queryFn: () => calibrationService.getCalibrationSchedules(filters),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCalibrationSchedule(id?: string) {
  return useQuery({
    queryKey: calibrationKeys.schedule(id || ''),
    queryFn: () => calibrationService.getCalibrationScheduleById(id!),
    enabled: !!id,
  });
}

export function useCreateCalibrationSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<CalibrationSchedule>) => calibrationService.createCalibrationSchedule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calibrationKeys.all });
      message.success('Calibration schedule created successfully');
    },
    onError: (e: any) => message.error(errorText(e, 'Failed to create calibration schedule')),
  });
}

export function useUpdateCalibrationSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CalibrationSchedule> }) =>
      calibrationService.updateCalibrationSchedule(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calibrationKeys.all });
      message.success('Calibration schedule updated');
    },
    onError: (e: any) => message.error(errorText(e, 'Failed to update calibration schedule')),
  });
}

export function useCancelCalibrationSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      calibrationService.cancelCalibrationSchedule(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calibrationKeys.all });
      message.success('Calibration schedule cancelled');
    },
    onError: (e: any) => message.error(errorText(e, 'Failed to cancel calibration schedule')),
  });
}

export function useCalibrationRecords(filters?: {
  status?: string;
  result?: string;
  equipment_id?: string;
  quality_review_status?: QualityReviewStatus;
}) {
  return useQuery({
    queryKey: calibrationKeys.records(filters),
    queryFn: () => calibrationService.getCalibrationRecords(filters),
    staleTime: 2 * 60 * 1000,
  });
}

export function useCalibrationRecord(id: string) {
  return useQuery({
    queryKey: calibrationKeys.record(id),
    queryFn: () => calibrationService.getCalibrationRecordById(id),
    enabled: !!id,
  });
}

export function useCreateCalibrationRecord() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CalibrationRecordInput) => calibrationService.createCalibrationRecord(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calibrationKeys.all });
      message.success('Calibration record created — awaiting Quality review');
    },
    onError: (e: any) => message.error(errorText(e, 'Failed to create calibration record')),
  });
}

export function useUpdateCalibrationRecord() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CalibrationRecordInput }) =>
      calibrationService.updateCalibrationRecord(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calibrationKeys.all });
      message.success('Calibration record updated');
    },
    onError: (e: any) => message.error(errorText(e, 'Failed to update calibration record')),
  });
}

export function useReviewCalibrationRecord() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decision, remarks }: { id: string; decision: 'ACCEPT' | 'REJECT'; remarks?: string }) =>
      calibrationService.reviewCalibrationRecord(id, decision, remarks),
    onSuccess: (_d, v) => {
      queryClient.invalidateQueries({ queryKey: calibrationKeys.all });
      message.success(v.decision === 'ACCEPT' ? 'Calibration accepted' : 'Calibration rejected');
    },
    onError: (e: any) => message.error(errorText(e, 'Failed to review calibration record')),
  });
}

export function useTmmdeEquipmentList(filters?: TmmdeEquipmentFilters) {
  return useQuery({
    queryKey: calibrationKeys.equipmentList(filters),
    queryFn: () => calibrationService.listEquipment(filters),
    staleTime: 60 * 1000,
  });
}

export function useTmmdeEquipment(assetId?: string) {
  return useQuery({
    queryKey: calibrationKeys.equipment(assetId || ''),
    queryFn: () => calibrationService.getEquipment(assetId!),
    enabled: !!assetId,
  });
}

export function useTmmdeEquipmentUsage(assetId?: string) {
  return useQuery({
    queryKey: [...calibrationKeys.equipment(assetId || ''), 'usage'] as const,
    queryFn: () => calibrationService.getEquipmentUsage(assetId!),
    enabled: !!assetId,
  });
}

export function useChangeTmmdeStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      assetId,
      action,
      reason,
      cause,
    }: {
      assetId: string;
      action: TmmdeStatusAction;
      reason?: string;
      cause?: TmmdeHoldCause;
    }) => calibrationService.changeEquipmentStatus(assetId, action, reason, cause),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calibrationKeys.all });
      message.success('Equipment status updated');
    },
    onError: (e: any) => message.error(errorText(e, 'Failed to change equipment status')),
  });
}

export function useRecordExternalVerification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ assetId, data }: { assetId: string; data: ExternalVerificationInput }) =>
      calibrationService.recordExternalVerification(assetId, data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: calibrationKeys.all });
      if (res.outcome === 'ACCEPTED') message.success('External-source verification accepted');
      else message.warning('Verification rejected — equipment quarantined');
    },
    onError: (e: any) => message.error(errorText(e, 'Failed to record verification')),
  });
}

export function useTmmdeKpis() {
  return useQuery({
    queryKey: [...calibrationKeys.all, 'equipment', 'kpis'] as const,
    queryFn: () => calibrationService.getEquipmentKpis(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useTmmdeImpactAssessments(filters?: { status?: string; asset_id?: string }, enabled = true) {
  return useQuery({
    queryKey: calibrationKeys.impactList(filters),
    queryFn: () => calibrationService.listImpactAssessments(filters),
    enabled,
  });
}

export function useTmmdeImpactAssessment(id?: string) {
  return useQuery({
    queryKey: calibrationKeys.impact(id || ''),
    queryFn: () => calibrationService.getImpactAssessment(id!),
    enabled: !!id,
  });
}

/** Wraps an impact-assessment action: refreshes the assessment and lists on success. */
export function useImpactAction<TVars>(
  fn: (vars: TVars) => Promise<unknown>,
  successText: string | ((res: any) => string)
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [...calibrationKeys.all, 'impact'] });
      queryClient.invalidateQueries({ queryKey: [...calibrationKeys.all, 'equipment'] });
      message.success(typeof successText === 'function' ? successText(res) : successText);
    },
    onError: (e: any) => message.error(errorText(e, 'Action failed')),
  });
}
